// src/screens/Admin/BannerRevenueTile.js

import React, { useMemo } from 'react';
import './BannerRevenueTile.css';

const formatNumber = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0';
  }

  return number.toLocaleString();
};

const formatPi = (value, decimals = 2) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0 Pi';
  }

  return `${number.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  })} Pi`;
};

const formatPercentage = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0%';
  }

  return `${number.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
};

const formatDecimal = (value, decimals = 2) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0';
  }

  return number.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

const getBannerName = (banner) => {
  return banner?.name || banner?.slug || 'No data yet';
};

const getBannerImage = (banner) => {
  return banner?.imageUrl || '';
};

const getPreviousStageRate = (currentValue, previousValue) => {
  const current = Number(currentValue) || 0;
  const previous = Number(previousValue) || 0;

  if (previous <= 0) {
    return 0;
  }

  return (current / previous) * 100;
};

function FunnelStage({
  label,
  value,
  percentage,
  isFirst = false,
  isLast = false,
}) {
  return (
    <div
      className={`banner-revenue-funnel-stage ${
        isLast ? 'is-final-stage' : ''
      }`}
    >
      <div className="banner-revenue-funnel-copy">
        <span>{label}</span>

        <strong>{formatNumber(value)}</strong>
      </div>

      {!isFirst && (
        <span className="banner-revenue-funnel-rate">
          {formatPercentage(percentage)} from previous
        </span>
      )}
    </div>
  );
}

function MetricCard({
  label,
  value,
  hint,
  featured = false,
  negative = false,
}) {
  return (
    <div
      className={`banner-revenue-metric-card ${
        featured ? 'is-featured' : ''
      } ${negative ? 'is-negative' : ''}`}
    >
      <span className="banner-revenue-metric-label">
        {label}
      </span>

      <strong>{value}</strong>

      {hint && (
        <span className="banner-revenue-metric-hint">
          {hint}
        </span>
      )}
    </div>
  );
}

function TopBannerCard({
  label,
  banner,
  metric,
}) {
  const imageUrl = getBannerImage(banner);
  const bannerName = getBannerName(banner);

  return (
    <div className="banner-revenue-top-card">
      <span className="banner-revenue-top-label">
        {label}
      </span>

      <div className="banner-revenue-top-content">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={bannerName}
            className="banner-revenue-top-image"
          />
        ) : (
          <div className="banner-revenue-top-placeholder">
            No image
          </div>
        )}

        <div className="banner-revenue-top-copy">
          <strong>{bannerName}</strong>
          <span>{metric}</span>
        </div>
      </div>
    </div>
  );
}

function BannerPerformanceRow({ banner }) {
  return (
    <div className="banner-revenue-performance-row">
      <div className="banner-revenue-performance-banner">
        {banner?.imageUrl ? (
          <img
            src={banner.imageUrl}
            alt={banner.name || 'Profile banner'}
          />
        ) : (
          <div className="banner-revenue-performance-placeholder" />
        )}

        <div>
          <strong>
            {banner?.name || banner?.slug || 'Unknown Banner'}
          </strong>

          <span>
            {banner?.collectionKey || 'Other'}
          </span>
        </div>
      </div>

      <div className="banner-revenue-performance-stat">
        <span>Preview Users</span>
        <strong>
          {formatNumber(banner?.uniquePreviewUsers)}
        </strong>
      </div>

      <div className="banner-revenue-performance-stat">
        <span>Preview Opens</span>
        <strong>
          {formatNumber(banner?.totalPreviewOpens)}
        </strong>
      </div>

      <div className="banner-revenue-performance-stat">
        <span>Period Purchases</span>
        <strong>
          {formatNumber(banner?.purchases)}
        </strong>
      </div>

      <div className="banner-revenue-performance-stat">
        <span>Preview Conversion</span>
        <strong>
          {formatPercentage(
            banner?.previewToPurchaseRate
          )}
        </strong>
      </div>

      <div className="banner-revenue-performance-stat">
        <span>Revenue</span>
        <strong>
          {formatPi(
            banner?.entitlementRevenuePi,
            4
          )}
        </strong>
      </div>
    </div>
  );
}

function CollectionRow({ collection }) {
  return (
    <div className="banner-revenue-collection-row">
      <div className="banner-revenue-collection-name">
        <strong>
          {collection?.collectionKey || 'Other'}
        </strong>
      </div>

      <div>
        <span>Preview Users</span>
        <strong>
          {formatNumber(
            collection?.uniquePreviewUsers
          )}
        </strong>
      </div>

      <div>
        <span>Period Purchases</span>
        <strong>
          {formatNumber(collection?.purchases)}
        </strong>
      </div>

      <div>
        <span>Preview Conversion</span>
        <strong>
          {formatPercentage(
            collection?.previewToPurchaseRate
          )}
        </strong>
      </div>

      <div>
        <span>Revenue</span>
        <strong>
          {formatPi(
            collection?.entitlementRevenuePi,
            4
          )}
        </strong>
      </div>
    </div>
  );
}

function BannerRevenueTile({
  data,
  loading = false,
  error = '',
  onRetry,
}) {
  const funnel = data?.funnel || {};
  const rates = data?.rates || {};
  const revenue = data?.revenue || {};
  const engagement = data?.engagement || {};
  const topBanners = data?.topBanners || {};

  const bannerPerformance = Array.isArray(
    data?.bannerPerformance
  )
    ? data.bannerPerformance
    : [];

  const collections = Array.isArray(
    data?.collections
  )
    ? data.collections
    : [];

  const funnelStages = useMemo(() => {
    const eligibleActiveUsers =
      Number(funnel?.eligibleActiveUsers) || 0;

    const screenVisitors =
      Number(funnel?.screenVisitors) || 0;

    const previewUsers =
      Number(funnel?.previewUsers) || 0;

    const previewTrialStarters =
      Number(
        funnel?.previewUsersWhoStartedTrial
      ) || 0;

    const previewPurchasers =
      Number(
        funnel?.previewUsersWhoPurchased
      ) || 0;

    return [
      {
        label: 'Eligible Active Users',
        value: eligibleActiveUsers,
        percentage: 100,
      },
      {
        label: 'Banner Screen Visitors',
        value: screenVisitors,
        percentage: getPreviousStageRate(
          screenVisitors,
          eligibleActiveUsers
        ),
      },
      {
        label: 'Preview Users',
        value: previewUsers,
        percentage: getPreviousStageRate(
          previewUsers,
          screenVisitors
        ),
      },
      {
        label: 'Trials After Preview',
        value: previewTrialStarters,
        percentage: getPreviousStageRate(
          previewTrialStarters,
          previewUsers
        ),
      },
      {
        label: 'Purchases After Preview',
        value: previewPurchasers,
        percentage: getPreviousStageRate(
          previewPurchasers,
          previewUsers
        ),
      },
    ];
  }, [funnel]);

  if (loading) {
    return (
      <section className="banner-revenue-tile">
        <div className="banner-revenue-loading">
          <div className="banner-revenue-loading-spinner" />

          <strong>Loading profile banner analytics...</strong>
          <span>Calculating funnel and revenue data.</span>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="banner-revenue-tile">
        <div className="banner-revenue-error">
          <strong>Could not load banner analytics</strong>
          <span>{error}</span>

          {typeof onRetry === 'function' && (
            <button
              type="button"
              onClick={onRetry}
            >
              Try Again
            </button>
          )}
        </div>
      </section>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <section className="banner-revenue-tile">
      <div className="banner-revenue-header">
        <div>
          <div className="banner-revenue-title-line">
            <h2>Profile Banners</h2>

            <span className="banner-revenue-live-pill">
              Live Experiment
            </span>
          </div>

          <p>
            Measure how active users discover, preview,
            trial and purchase profile banners.
          </p>
        </div>

        <div className="banner-revenue-header-meta">
          <span>
            {data?.range?.isAllTime
              ? 'All Time'
              : `${data?.range?.days || 30} Days`}
          </span>

          <strong>
            {data?.range?.network || 'mainnet'}
          </strong>
        </div>
      </div>

      <div className="banner-revenue-section">
        <div className="banner-revenue-section-heading">
          <div>
            <span className="banner-revenue-section-eyebrow">
              User journey
            </span>

            <h3>Conversion Funnel</h3>
          </div>

          <span className="banner-revenue-section-note">
            Attributed user journey
          </span>
        </div>

        <div className="banner-revenue-funnel">
          {funnelStages.map((stage, index) => (
            <FunnelStage
              key={stage.label}
              label={stage.label}
              value={stage.value}
              percentage={stage.percentage}
              isFirst={index === 0}
              isLast={index === funnelStages.length - 1}
            />
          ))}
        </div>

        <div className="banner-revenue-rate-grid">
          <MetricCard
            label="Discovery Rate"
            value={formatPercentage(
              rates?.discoveryRate
            )}
            hint="Visitors from eligible active users"
          />

          <MetricCard
            label="Preview Rate"
            value={formatPercentage(
              rates?.previewRate
            )}
            hint="Preview users from screen visitors"
          />

          <MetricCard
            label="Trial From Preview"
            value={formatPercentage(
              rates?.trialFromPreviewRate
            )}
            hint="Preview users who later started a trial"
          />

          <MetricCard
            label="Purchase From Preview"
            value={formatPercentage(
              rates?.purchaseFromPreviewRate
            )}
            hint="Preview users who later purchased"
          />

          <MetricCard
            label="Period Trial Starters"
            value={formatNumber(
              funnel?.trialStarters
            )}
            hint="All unique trial starters during this period"
          />

          <MetricCard
            label="Period Purchasers"
            value={formatNumber(
              funnel?.purchasers
            )}
            hint="All unique purchasers during this period"
          />

          <MetricCard
            label="Trial To Purchase"
            value={formatPercentage(
              rates?.trialToPurchaseRate
            )}
            hint="Period trial users who purchased"
          />

          <MetricCard
            label="Purchase From Active"
            value={formatPercentage(
              rates?.purchaseFromActiveRate
            )}
            hint="Period purchasers from eligible active users"
          />
        </div>
      </div>

      <div className="banner-revenue-section">
        <div className="banner-revenue-section-heading">
          <div>
            <span className="banner-revenue-section-eyebrow">
              Financial performance
            </span>

            <h3>Revenue</h3>
          </div>
        </div>

        <div className="banner-revenue-metrics-grid">
          <MetricCard
            label="Net Revenue"
            value={formatPi(
              revenue?.netPi,
              4
            )}
            hint="Gross revenue minus refunds"
            featured
          />

          <MetricCard
            label="Gross Revenue"
            value={formatPi(
              revenue?.grossPi,
              4
            )}
            hint="Confirmed banner purchases"
          />

          <MetricCard
            label="Refunded"
            value={formatPi(
              revenue?.refundedPi,
              4
            )}
            hint={`${formatNumber(
              revenue?.refundTransactions
            )} refund transactions`}
            negative
          />

          <MetricCard
            label="Sales"
            value={formatNumber(
              revenue?.sales
            )}
            hint={`${formatNumber(
              revenue?.uniquePurchasers
            )} unique purchasers`}
          />

          <MetricCard
            label="Average Sale"
            value={formatPi(
              revenue?.averageSalePi,
              4
            )}
            hint="Gross revenue per sale"
          />

          <MetricCard
            label="Period Revenue Per Visitor"
            value={formatPi(
              revenue?.revenuePerVisitorPi,
              4
            )}
            hint="Period net revenue divided by screen visitors"
          />

          <MetricCard
            label="Period Revenue Per Preview User"
            value={formatPi(
              revenue?.revenuePerPreviewUserPi,
              4
            )}
            hint="Period net revenue divided by preview users"
          />

          <MetricCard
            label="Revenue Per Active User"
            value={formatPi(
              revenue?.revenuePerActiveUserPi,
              4
            )}
            hint="Net revenue per eligible active user"
          />
        </div>
      </div>

      <div className="banner-revenue-section">
        <div className="banner-revenue-section-heading">
          <div>
            <span className="banner-revenue-section-eyebrow">
              Interest signals
            </span>

            <h3>Engagement</h3>
          </div>
        </div>

        <div className="banner-revenue-rate-grid">
          <MetricCard
            label="Screen Sessions"
            value={formatNumber(
              engagement?.screenSessions
            )}
            hint="Deduplicated collection visits"
          />

          <MetricCard
            label="Preview Opens"
            value={formatNumber(
              engagement?.totalPreviewOpens
            )}
            hint="Total preview drawer opens"
          />

          <MetricCard
            label="Sessions Per Visitor"
            value={formatDecimal(
              engagement?.averageSessionsPerVisitor,
              2
            )}
            hint="Average return frequency"
          />

          <MetricCard
            label="Previews Per User"
            value={formatDecimal(
              engagement?.averagePreviewsPerVisitor,
              2
            )}
            hint="Average banners previewed"
          />
        </div>
      </div>

      <div className="banner-revenue-section">
        <div className="banner-revenue-section-heading">
          <div>
            <span className="banner-revenue-section-eyebrow">
              Strongest signals
            </span>

            <h3>Top Banners</h3>
          </div>
        </div>

        <div className="banner-revenue-top-grid">
          <TopBannerCard
            label="Most Previewed"
            banner={topBanners?.mostPreviewed}
            metric={`${formatNumber(
              topBanners?.mostPreviewed
                ?.uniquePreviewUsers
            )} preview users`}
          />

          <TopBannerCard
            label="Most Purchased"
            banner={topBanners?.mostPurchased}
            metric={`${formatNumber(
              topBanners?.mostPurchased
                ?.purchases
            )} purchases`}
          />

          <TopBannerCard
            label="Highest Conversion"
            banner={topBanners?.highestConversion}
            metric={formatPercentage(
              topBanners?.highestConversion
                ?.previewToPurchaseRate
            )}
          />
        </div>
      </div>

      <div className="banner-revenue-section">
        <div className="banner-revenue-section-heading">
          <div>
            <span className="banner-revenue-section-eyebrow">
              Banner comparison
            </span>

            <h3>Banner Performance</h3>
          </div>

          <span className="banner-revenue-section-note">
            {formatNumber(
              bannerPerformance.length
            )}{' '}
            banners
          </span>
        </div>

        {bannerPerformance.length === 0 ? (
          <div className="banner-revenue-empty">
            <strong>No banner performance data yet</strong>
            <span>
              Preview and purchase data will appear here.
            </span>
          </div>
        ) : (
          <div className="banner-revenue-performance-list">
            {bannerPerformance.map((banner) => (
              <BannerPerformanceRow
                key={
                  banner?.bannerId ||
                  banner?.slug ||
                  banner?.name
                }
                banner={banner}
              />
            ))}
          </div>
        )}
      </div>

      <div className="banner-revenue-section">
        <div className="banner-revenue-section-heading">
          <div>
            <span className="banner-revenue-section-eyebrow">
              Theme comparison
            </span>

            <h3>Collections</h3>
          </div>
        </div>

        {collections.length === 0 ? (
          <div className="banner-revenue-empty">
            <strong>No collection data yet</strong>
            <span>
              Collection performance will appear here.
            </span>
          </div>
        ) : (
          <div className="banner-revenue-collection-list">
            {collections.map((collection) => (
              <CollectionRow
                key={
                  collection?.collectionKey ||
                  'other'
                }
                collection={collection}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default BannerRevenueTile;
